import React, { FunctionComponent } from 'react';
import {
  Card,
  CardContent,
  Dialog,
  DialogContent,
  DialogTitle,
  Grid,
  IconButton,
  Stack,
  Typography,
} from '@mui/material';
import { Close as CloseIcon } from '@mui/icons-material';
import { Moment } from 'moment';

import { useAppSelector } from '../../../../../../../hooks';
import { calculateTotalAmountByCurrencyAndAmount } from '../../../../../../../helper';
import { getMeetingRequestsState } from '../../../../../../../store/V2/MeetingRequestsSlice';

interface PendingTutorAmountBreakDownModalProps {
  open: boolean;
  onClose: () => void;
  startDate: Moment | null;
  endDate: Moment | null;
}

const dateFormatString = 'ddd MMM DD YYYY';

const CustomCard = ({
  title = '',
  description = '',
}: {
  title?: string;
  description?: string;
}) => {
  return (
    <Card
      elevation={0}
      sx={{
        backgroundColor: '#F8FAFC',
        borderRadius: '8px',
      }}
    >
      <CardContent style={{ padding: '8px 16px' }}>
        <Stack
          direction="row"
          justifyContent="space-between"
          alignItems="center"
        >
          <Typography fontSize="14px" fontWeight={500} color="#647488">
            {title}
          </Typography>
          <Typography fontSize="14px" fontWeight={600} color="#1E293B">
            {description}
          </Typography>
        </Stack>
      </CardContent>
    </Card>
  );
};

const TotalCard = ({ total }: { total: string }) => {
  return (
    <Card
      elevation={0}
      sx={{
        backgroundColor: '#1E293B',
        borderRadius: '8px',
      }}
    >
      <CardContent style={{ padding: '8px 16px' }}>
        <Typography
          fontSize="14px"
          fontWeight={600}
          color="#F8FAFC"
          textAlign="center"
        >
          {total}
        </Typography>
      </CardContent>
    </Card>
  );
};

const PendingTutorAmountBreakDownModal: FunctionComponent<PendingTutorAmountBreakDownModalProps> = ({
  open,
  onClose,
  startDate,
  endDate,
}) => {
  // Store
  const meetingRequestsState = useAppSelector(getMeetingRequestsState);

  const tutorAmountData =
    meetingRequestsState.meetingRequestListData &&
    meetingRequestsState.meetingRequestListData.tutorAmountData
      ? meetingRequestsState.meetingRequestListData.tutorAmountData
      : undefined;

  let descriptionText = 'Detailed breakdown of your total payable amount';
  let totalAmount = '00';
  let pendingAmount = '00';
  let tutorPenaltyAmount = '00';
  let studentPenaltyAmount = '00';
  let penaltyWaiverAmount = '00';

  if (startDate && endDate) {
    descriptionText += ` for ${startDate.format(
      dateFormatString
    )} to ${endDate.format(dateFormatString)}`;
  } else if (startDate && !endDate) {
    descriptionText += ` from ${startDate.format(dateFormatString)}`;
  } else if (!startDate && endDate) {
    descriptionText += ` upto ${endDate.format(dateFormatString)}`;
  }

  if (tutorAmountData) {
    totalAmount =
      calculateTotalAmountByCurrencyAndAmount(
        tutorAmountData.pendingAmountData
      ) || '00';

    pendingAmount =
      calculateTotalAmountByCurrencyAndAmount(
        tutorAmountData.pendingTotalData
      ) || '00';

    tutorPenaltyAmount =
      calculateTotalAmountByCurrencyAndAmount(
        tutorAmountData.pendingTutorPenaltyData
      ) || '00';

    studentPenaltyAmount =
      calculateTotalAmountByCurrencyAndAmount(
        tutorAmountData.pendingStudentPenaltyData
      ) || '00';

    penaltyWaiverAmount =
      calculateTotalAmountByCurrencyAndAmount(
        tutorAmountData.pendingPenaltyWaiverData
      ) || '00';
  }

  return (
    <Dialog
      fullWidth
      maxWidth="sm"
      open={open}
      onClose={onClose}
      PaperProps={{
        sx: { borderRadius: '20px', maxWidth: '480px', maxHeight: '600px' },
      }}
    >
      <DialogTitle
        sx={{
          m: 0,
          p: { xs: '15px', sm: '30px' },
          color: '#000000',
          fontWeight: 600,
          fontSize: '16px',
        }}
      >
        Total Payable Amount
      </DialogTitle>
      <IconButton
        aria-label="close"
        onClick={onClose}
        sx={{
          position: 'absolute',
          right: { xs: '7px', sm: '22px' },
          top: { xs: '7px', sm: '22px' },
        }}
      >
        <CloseIcon sx={{ color: '#000000' }} />
      </IconButton>
      <DialogContent
        sx={{ p: { xs: '0 15px 15px 15px', sm: '0 30px 30px 30px' } }}
      >
        <div>
          <Typography fontSize="14px" fontWeight={500} color="#647488">
            {descriptionText}
          </Typography>
        </div>
        <div style={{ paddingTop: '30px' }}>
          <Grid container rowSpacing="8px">
            <Grid item xs={12}>
              <CustomCard
                title="Total Fees Earned"
                description={pendingAmount}
              />
            </Grid>
            <Grid item xs={12}>
              <CustomCard
                title="Tutor penalties (-)"
                description={tutorPenaltyAmount}
              />
            </Grid>
            <Grid item xs={12}>
              <CustomCard
                title="Student penalties (+)"
                description={studentPenaltyAmount}
              />
            </Grid>
            <Grid item xs={12}>
              <CustomCard
                title="Payment adjustments (+)"
                description={penaltyWaiverAmount}
              />
            </Grid>
            <Grid item xs={12}>
              <TotalCard total={`Total Paid Amount (${totalAmount})`} />
            </Grid>
          </Grid>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default PendingTutorAmountBreakDownModal;
